import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, InputNumber, Select, message } from 'antd';
import axios from 'axios';
import ProductCategorySelect from '../../components/ProductCategorySelect';
import TaxProfileSelect from '../../components/TaxProfileSelect';

const { Option } = Select;
const { TextArea } = Input;

const ProductForm = ({ visible, onCancel, onSuccess, initialValues, mode }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (visible && mode === 'edit' && initialValues) {
            fetchDetails(initialValues.id);
        } else {
            form.resetFields();
            form.setFieldsValue({
                type: '0',
                status: '1'
            });
        }
    }, [visible, initialValues, mode]);

    const fetchDetails = async (id) => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const res = await axios.get(`/api/products/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const data = res.data;
            form.setFieldsValue({
                ref: data.ref,
                label: data.label,
                type: data.type,
                price: data.price_ttc,
                cost_price: data.cost_price,
                tax_profile_id: data.tax_profile_id,
                description: data.description,
                status: data.status,
                barcode: data.barcode,
                note: data.note,
                category_id: data.category_id || undefined
            });
        } catch (err) {
            console.error(err);
            message.error('Failed to fetch full product details');
            form.setFieldsValue(initialValues);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (values) => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}` };

            // Transform values if needed
            // Dolibarr expects 'type' as string/int? usually 0 or 1.

            if (mode === 'create') {
                await axios.post('/api/products', values, { headers });
                message.success('Product created');
            } else {
                await axios.put(`/api/products/${initialValues.id}`, values, { headers });
                message.success('Product updated');
            }
            onSuccess();
        } catch (error) {
            console.error(error);
            message.error('Operation failed');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            title={mode === 'create' ? 'Create Product' : 'Edit Product'}
            open={visible}
            onCancel={onCancel}
            onOk={() => form.submit()}
            confirmLoading={loading}
            destroyOnClose
            width={600}
        >
            <Form form={form} layout="vertical" onFinish={handleSubmit}>
                <div style={{ display: 'flex', gap: '16px' }}>
                    <Form.Item name="ref" label="Reference (SKU)" rules={[{ required: true }]} style={{ flex: 1 }}>
                        <Input disabled={mode === 'edit'} placeholder="Auto or Custom SKU" />
                    </Form.Item>
                    <Form.Item name="barcode" label="Barcode" style={{ flex: 1 }}>
                        <Input placeholder="EAN13 / UPC" />
                    </Form.Item>
                </div>

                <div style={{ display: 'flex', gap: '16px' }}>
                    <Form.Item name="label" label="Label / Name" rules={[{ required: true }]} style={{ flex: 2 }}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="type" label="Type" style={{ flex: 1 }}>
                        <Select>
                            <Option value="0">Product</Option>
                            <Option value="1">Service</Option>
                        </Select>
                    </Form.Item>
                </div>

                <div style={{ display: 'flex', gap: '16px' }}>
                    <Form.Item name="price" label="Selling Price (TTC)" rules={[{ required: true }]} style={{ flex: 1 }}>
                        <InputNumber style={{ width: '100%' }} precision={3} min={0} addonAfter="JOD" />
                    </Form.Item>
                    <Form.Item name="cost_price" label="Cost Price" style={{ flex: 1 }}>
                        <InputNumber style={{ width: '100%' }} precision={3} min={0} addonAfter="JOD" />
                    </Form.Item>
                    <Form.Item name="tax_profile_id" label="Tax Profile" rules={[{ required: true }]} style={{ flex: 1 }}>
                        <TaxProfileSelect />
                    </Form.Item>
                </div>

                <div style={{ display: 'flex', gap: '16px' }}>
                    <Form.Item name="category_id" label="Category / Condition" style={{ flex: 1 }}>
                        <ProductCategorySelect />
                        <div style={{ fontSize: '10px', color: '#888', marginTop: 4 }}>Manage "New" and "Used" categories in Dolibarr to see them here.</div>
                    </Form.Item>
                    {mode === 'create' && (
                        <Form.Item name="initial_stock" label="Initial Stock" style={{ width: '120px' }}>
                            <InputNumber style={{ width: '100%' }} min={0} placeholder="0" />
                        </Form.Item>
                    )}
                    <Form.Item name="status" label="Status" style={{ width: '120px' }}>
                        <Select>
                            <Option value="1">Sale</Option>
                            <Option value="0">Not For Sale</Option>
                        </Select>
                    </Form.Item>
                </div>

                <Form.Item name="description" label="Description">
                    <TextArea rows={2} />
                </Form.Item>

                <Form.Item name="note" label="Private Note">
                    <TextArea rows={2} placeholder="Internal notes" />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default ProductForm;
